import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { downscaleImage } from '../../utils/image';
import { CircularProgress } from '@mui/material';
import { AutoAwesomeRounded, DrawRounded, CollectionsRounded, LightbulbRounded } from '@mui/icons-material';
import { useAuth } from '../../context/AuthContext';
import { useChat } from '../../context/ChatContext';
import { getPipelineSteps, getConversationMemory, getConversationQuizEligibility, getStemImages, getPushedStemImages, StemModuleImage } from '../../services/api';
import StemImagePanel from './StemImagePanel';
import { QuizEligibility } from '../../types';
import { PipelineStep } from '../PipelineStepsModal';

import ConversationSidebar from '../ConversationSidebar';
import ChatHeader from './ChatHeader';
import MessageList from './MessageList';
import MessageInput from './MessageInput';
import DrawCanvasOverlay from './DrawCanvasOverlay';
import ChatModals from './ChatModals';
import FeedbackModal from '../FeedbackModal';
import DebugInfo from '../DebugInfo';
import ExplorationPanel from '../ExplorationPanel';
import CuriosityScoreIndicator from '../common/CuriosityScoreIndicator';
import QuizPanel from '../QuizPanel';

interface ChatInterfaceProps {
  mode: 'chat' | 'test-prompt';
}

const ChatInterface: React.FC<ChatInterfaceProps> = ({ mode }) => {
  const {
    messages,
    currentConversationId,
    currentVisitNumber,
    currentPromptVersionId,
    isLoadingMessages,
    isSendingMessage,
    error: chatError,
    handleSendMessageWithAutoConversation,
    isBrainProcessing,
    isConfigViewActive,
    preparationStatus,
    isPreparingConversation,
    isInitializingForNewUser,
    fetchConversations,
    selectConversation,
  } = useChat();

  const [newMessage, setNewMessage] = useState('');
  // Attached image (photo or notepad drawing) for the next turn: the downscaled
  // blob we send, plus an object-URL preview shown in the composer.
  const [attachedImage, setAttachedImage] = useState<Blob | null>(null);
  const [attachedPreview, setAttachedPreview] = useState<string | null>(null);
  // How to treat the attached image: 'ask' (explain / discuss) or 'check_work'
  // (Socratically review the student's own attempt).
  const [imageMode, setImageMode] = useState<'ask' | 'check_work'>('ask');
  const [showDraw, setShowDraw] = useState(false);
  // STEM module page images — shown in a right-side panel when this chat is on a
  // STEM module that has rendered pages. Shown for the module the student picked
  // (their first message = the topic) OR the module the teacher PUSHED as today's
  // session (resolved server-side) — so the panel opens either way. An explicit
  // pick wins; the pushed module is the fallback. `stemPanelHidden` lets them dismiss it.
  const [stemImages, setStemImages] = useState<StemModuleImage[]>([]);
  const [stemTitle, setStemTitle] = useState<string | null>(null);
  const [stemPanelHidden, setStemPanelHidden] = useState(false);
  const firstUserTopic = (messages.find((m) => m.is_user)?.content || '').trim();
  const hasConversation = !!currentConversationId;
  useEffect(() => {
    let cancelled = false;
    const apply = (imgs: StemModuleImage[], title: string | null) => {
      if (cancelled) return;
      setStemImages(imgs);
      setStemTitle(imgs.length ? title : null);
      setStemPanelHidden(false);
    };
    (async () => {
      // 1) Module the student explicitly picked (first message = topic).
      if (firstUserTopic) {
        const picked = await getStemImages(firstUserTopic);
        if (picked.length) { apply(picked, firstUserTopic); return; }
      }
      // 2) Fallback: module the teacher pushed as today's session.
      if (hasConversation) {
        const pushed = await getPushedStemImages();
        if (pushed.images.length) { apply(pushed.images, pushed.title); return; }
      }
      apply([], null);
    })();
    return () => { cancelled = true; };
  }, [firstUserTopic, hasConversation]);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  const [showQuizPanel, setShowQuizPanel] = useState(false);
  
  // Pipeline modal state
  const [showPipelineModal, setShowPipelineModal] = useState(false);
  const [pipelineSteps, setPipelineSteps] = useState<PipelineStep[]>([]);
  const [isLoadingSteps, setIsLoadingSteps] = useState(false);
  const [pipelineError, setPipelineError] = useState<string | null>(null);

  // Memory modal state
  const [showMemoryModal, setShowMemoryModal] = useState(false);
  const [memoryData, setMemoryData] = useState<any>(null);
  const [isLoadingMemory, setIsLoadingMemory] = useState(false);
  const [memoryError, setMemoryError] = useState<string | null>(null);

  // Exploration panel state
  const [showExplorationPanel, setShowExplorationPanel] = useState(false);
  const [explorationDirections, setExplorationDirections] = useState<string[]>([]);
  const [explorationPrompt, setExplorationPrompt] = useState<string | undefined>(undefined);
  const [curiosityTip, setCuriosityTip] = useState<string | undefined>(undefined);
  const lastShownAiIdRef = React.useRef<number | string | null>(null);
  const lastTipFetchedAiIdRef = React.useRef<number | string | null>(null);

  const { user, language } = useAuth();
  const location = useLocation();
  const [quizEligibility, setQuizEligibility] = React.useState<QuizEligibility | null>(null);

  // If we arrived here from a "Discuss with AI" button on an assigned topic,
  // the backend already created a topic-anchored conversation; just select it.
  // Refresh the conversation list so the new one shows in the sidebar.
  // Consume the state once so a page refresh doesn't keep re-selecting.
  React.useEffect(() => {
    const sel = (location.state as { selectConversationId?: number } | null)?.selectConversationId;
    if (sel) {
      fetchConversations().finally(() => selectConversation(sel));
      window.history.replaceState({}, '');
    }
    // Intentionally only run once on mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // The LATEST score, not the highest. The number now means how much of the
  // topic the student has actually understood, so it has to be able to fall —
  // a Math.max reduce turns it into a high-water mark that can only ever climb,
  // which is what made the old engagement meter meaningless as a measurement.
  const curiosityScore = React.useMemo(() => {
    if (!messages || messages.length === 0) return 0;
    for (let i = messages.length - 1; i >= 0; i--) {
      const message = messages[i];
      if (!message.is_user && typeof message.curiosity_score === 'number') {
        return message.curiosity_score;
      }
    }
    return 0;
  }, [messages]);

  const MIN_USER_MESSAGES_FOR_QUIZ = 2;

  const localUserMessageCount = React.useMemo(() => {
    if (!messages?.length) return 0;
    return messages.filter((m) => m.is_user).length;
  }, [messages]);

  const showQuizButton = React.useMemo(() => {
    if (!currentConversationId) return false;
    if (quizEligibility?.has_new_content) return true;
    if (quizEligibility?.has_quiz) return true;
    if (quizEligibility?.eligible) return true;
    return localUserMessageCount >= MIN_USER_MESSAGES_FOR_QUIZ;
  }, [currentConversationId, quizEligibility, localUserMessageCount]);

  const quizButtonLabel = React.useMemo(() => {
    // Coach is pushing the end-of-topic quiz — invite it explicitly.
    if (quizEligibility?.coach_suggests_quiz && quizEligibility?.quiz_status !== 'completed') {
      return quizEligibility?.quiz_status === 'ready' ? 'Take the quiz 🎯' : 'Quick quiz? 🎯';
    }
    if (quizEligibility?.has_new_content) return 'Update quiz';
    if (quizEligibility?.quiz_status === 'completed') return 'View quiz';
    if (quizEligibility?.quiz_status === 'ready') return 'Continue quiz';
    // 'generating' = an LLM call is in-flight (this user or another client).
    // The destination page will show a spinner; the chat button reflects that.
    if (quizEligibility?.quiz_status === 'generating') return 'Building quiz…';
    // 'failed' = last LLM generation broke; QuizPage shows a recovery card.
    if (quizEligibility?.quiz_status === 'failed') return 'Retry quiz';
    return 'Take quiz';
  }, [quizEligibility]);

  // When the coach concludes the picked topic it flags coach_suggests_quiz;
  // auto-open the quiz panel ONCE per conversation so the student is prompted
  // to take it (they can still close it — we won't reopen).
  const autoPushedQuizRef = React.useRef<Set<number>>(new Set());
  React.useEffect(() => {
    if (!currentConversationId) return;
    if (
      quizEligibility?.coach_suggests_quiz &&
      !showQuizPanel &&
      !autoPushedQuizRef.current.has(currentConversationId)
    ) {
      autoPushedQuizRef.current.add(currentConversationId);
      setShowQuizPanel(true);
    }
  }, [quizEligibility?.coach_suggests_quiz, currentConversationId, showQuizPanel]);

  React.useEffect(() => {
    if (!currentConversationId) {
      setQuizEligibility(null);
      return;
    }
    // Use AbortController so older eligibility requests are actually cancelled
    // at the network layer when messages update rapidly (e.g. during streaming).
    // The previous `let cancelled = false` flag only suppressed the response —
    // the requests still hit the network. Matches the pattern in QuizPage.tsx +
    // QuizPanel.tsx + the api.ts wrappers that now accept an AbortSignal.
    const controller = new AbortController();
    getConversationQuizEligibility(currentConversationId, controller.signal)
      .then(setQuizEligibility)
      .catch((err: any) => {
        // Cancellations aren't real errors — the next effect run will fire
        // its own request. axios rejects (not resolves) on abort, so the
        // CanceledError reaches us here, not in the .then() handler above.
        if (controller.signal.aborted) return;
        if (err?.name === 'CanceledError' || err?.code === 'ERR_CANCELED') return;
        // Keep banner visible via local message count if the API is unavailable.
        setQuizEligibility(null);
      });
    return () => {
      controller.abort();
    };
    // Use `messages.length` (not the full `messages` array) as the dep. During
    // AI streaming, setMessages([...messages, updated]) fires on EVERY token,
    // creating a new array identity each time. With AbortController this would
    // tear down + re-fire eligibility GETs per token — a thundering herd that
    // the prior `cancelled` flag pattern hid by just suppressing responses.
    // Eligibility only changes when a NEW message arrives (length increment),
    // so depending on `.length` correctly dedupes intra-message updates.
  }, [currentConversationId, messages.length]);

  // Check for debug mode
  const queryParams = new URLSearchParams(location.search);
  const isDebugMode = queryParams.get('debug') === 'true';

  // Effect to auto-load exploration directions for latest AI message in debug mode
  React.useEffect(() => {
    if (!isDebugMode || !messages || messages.length === 0) return;

    const lastAi = [...messages].reverse().find(m => !m.is_user && m.id != null);
    if (!lastAi) return;

    // Streamed messages carry a client-side Date.now() placeholder id until the
    // history refresh brings the real DB id — polling the backend with it is a
    // guaranteed 404 (epoch-ms ~1.7e12 vs. sequential DB ids). Skip those; the
    // effect re-runs once the real id arrives.
    if (typeof lastAi.id === 'number' && lastAi.id > 1e11) return;

    // avoid refetching for same message
    if (lastShownAiIdRef.current === lastAi.id) return;
    lastShownAiIdRef.current = lastAi.id as number | string;

    (async () => {
      try {
        const steps: PipelineStep[] = await getPipelineSteps(lastAi.id as number | string);
        const explorationStep = steps.find(s => s.name === 'exploration_directions_evaluation');

        if (explorationStep && Array.isArray(explorationStep.directions)) {
          setExplorationDirections(explorationStep.directions || []);
          setExplorationPrompt(explorationStep.prompt || undefined);
          setShowExplorationPanel(true);
          // Also update the tip in debug mode
          setCuriosityTip(explorationStep.curiosity_tip || undefined);
        } else {
          // If missing, hide panel
          setExplorationDirections([]);
          setExplorationPrompt(undefined);
          setShowExplorationPanel(false);
        }
      } catch (e) {
        // On error, do not block chat; simply don't show panel
        setShowExplorationPanel(false);
      }
    })();
  }, [messages, isDebugMode]);

  // Effect to fetch curiosity tip for the score indicator (non-debug mode)
  React.useEffect(() => {
    if (isDebugMode || !messages || messages.length === 0) return;

    const lastAi = [...messages].reverse().find(m => !m.is_user && m.id != null);
    if (!lastAi) return;

    // Same temp-id guard as the exploration-panel effect above.
    if (typeof lastAi.id === 'number' && lastAi.id > 1e11) return;

    // avoid refetching for same message
    if (lastTipFetchedAiIdRef.current === lastAi.id) return;
    lastTipFetchedAiIdRef.current = lastAi.id as number | string;

    (async () => {
      try {
        const steps: PipelineStep[] = await getPipelineSteps(lastAi.id as number | string);
        const explorationStep = steps.find(s => s.name === 'exploration_directions_evaluation');

        if (explorationStep?.curiosity_tip) {
          setCuriosityTip(explorationStep.curiosity_tip);
        }
      } catch (e) {
        // On error, don't update the tip - fallback will be used
      }
    })();
  }, [messages, isDebugMode]);

  const clearAttachedImage = () => {
    if (attachedPreview) URL.revokeObjectURL(attachedPreview);
    setAttachedImage(null);
    setAttachedPreview(null);
    setImageMode('ask');
  };

  // Stage an image blob (from a photo pick or the notepad) for the next turn.
  const stageImageBlob = (blob: Blob) => {
    if (attachedPreview) URL.revokeObjectURL(attachedPreview);
    setAttachedImage(blob);
    setAttachedPreview(URL.createObjectURL(blob));
  };

  const handlePickImage = async (file: File) => {
    stageImageBlob(await downscaleImage(file));
  };

  // Notepad drawing → same attachment pipeline as a photo.
  const handleNotepadSave = (blob: Blob) => {
    stageImageBlob(blob);
  };

  const handleFormSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    // A turn is sendable with text OR an attached image (or both).
    if ((!newMessage.trim() && !attachedImage) || !user || isSendingMessage) return;

    const contentToSend = newMessage.trim();
    const imageToSend = attachedImage;
    const modeToSend = imageMode;
    setNewMessage('');
    clearAttachedImage();

    await handleSendMessageWithAutoConversation(contentToSend, mode, imageToSend, imageToSend ? modeToSend : null);
  };

  // Send a given piece of text directly (used by voice input to auto-send the
  // transcript). Bypasses `newMessage` state to avoid a stale-value race.
  const handleAutoSend = async (text: string) => {
    const contentToSend = text.trim();
    if (!contentToSend || !user || isSendingMessage) return;
    setNewMessage('');
    await handleSendMessageWithAutoConversation(contentToSend, mode);
  };

  const handleViewPipelineSteps = async (messageId: number | string) => {
    if (!messageId) return;
    setIsLoadingSteps(true);
    setPipelineError(null);
    setPipelineSteps([]);

    try {
      const stepsData: PipelineStep[] = await getPipelineSteps(messageId);
      setPipelineSteps(stepsData);
      setShowPipelineModal(true);
    } catch (error: any) {
      console.error("Error fetching pipeline steps:", error);
      setPipelineError(error.message || "An unknown error occurred while fetching steps.");
      setShowPipelineModal(true);
    } finally {
      setIsLoadingSteps(false);
    }
  };

  // Calculate total processing time from steps (sum of all time_taken values)
  const totalProcessingTime = React.useMemo(() => {
    if (!pipelineSteps || pipelineSteps.length === 0) return null;
    const total = pipelineSteps.reduce((sum, step) => {
      if (step.time_taken !== null && step.time_taken !== undefined) {
        return sum + step.time_taken;
      }
      return sum;
    }, 0);
    return total > 0 ? total : null;
  }, [pipelineSteps]);

  const handleViewMemory = async () => {
    if (!currentConversationId) return;
    setIsLoadingMemory(true);
    setMemoryError(null);
    setMemoryData(null);

    try {
      const data = await getConversationMemory(currentConversationId);
      setMemoryData(data);
    } catch (error: any) {
      setMemoryError(error.message || "An unknown error occurred while fetching memory.");
    } finally {
      setIsLoadingMemory(false);
      setShowMemoryModal(true);
    }
  };

  // Sidebar enabled — Claude/ChatGPT-style conversation history
  const shouldShowSidebar = !!user;

  // Show onboarding loading screen during conversation preparation (all visits)
  if (isInitializingForNewUser || isPreparingConversation) {
    // Determine loading message based on preparation status
    let loadingMessage = "Your personal learning companion is preparing to meet you...";
    if (preparationStatus === "generating_memory") {
      loadingMessage = "Reviewing your previous conversations...";
    } else if (preparationStatus === "generating_persona") {
      loadingMessage = "Understanding your learning style...";
    }
    
    return (
      <div className="flex h-screen-mobile main-gradient-bg items-center justify-center px-6">
        <div className="w-full max-w-md rounded-3xl border border-violet-200 bg-white/95 p-8 text-center shadow-sm">
          <div className="mb-4 inline-flex h-14 w-14 items-center justify-center rounded-2xl bg-violet-100 text-violet-700 animate-twinkle">
            <AutoAwesomeRounded />
          </div>
          <div className="mb-6 flex justify-center">
            <CircularProgress size={24} sx={{ color: '#7c3aed' }} />
          </div>

          <h2 className="mb-3 text-2xl font-semibold text-slate-900">
            Welcome to Curiosity Coach!
          </h2>
          <p className="mb-2 text-base text-slate-600 sm:text-lg">
            {loadingMessage}
          </p>
          <div className="mt-4 text-sm text-violet-700">
            We’re getting your coach ready for a thoughtful first conversation.
          </div>
          <div className="mt-2 text-sm text-slate-500">
            This can take a minute or two the first time.
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-screen-mobile main-gradient-bg">
      {/* Mobile overlay */}
      {shouldShowSidebar && isSidebarOpen && (
        <div 
          className="sidebar-overlay"
          onClick={() => setIsSidebarOpen(false)}
        />
      )}

      {/* Sidebar - only show for visit 4+ */}
      {shouldShowSidebar && (
        <div className={`
          sidebar-container
          ${isSidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
        `}>
          <ConversationSidebar
            onConversationSelect={() => setIsSidebarOpen(false)}
            onOpenFeedbackModal={() => {
              setShowFeedbackModal(true);
              setIsSidebarOpen(false);
            }}
          />
        </div>
      )}
      
      {/* Chat column — a flex ROW so the whiteboard can dock on the right */}
      <div className="flex flex-row h-screen-mobile flex-1 min-w-0">
        {/* Chat stack (header + messages + composer) — shrinks when the whiteboard opens */}
        <div className="relative flex flex-col flex-1 min-w-0 h-full">
        {/* Mobile header */}
        <ChatHeader
          isSidebarOpen={isSidebarOpen}
          onToggleSidebar={shouldShowSidebar ? () => setIsSidebarOpen(!isSidebarOpen) : undefined}
          isDebugMode={isDebugMode}
          currentConversationId={currentConversationId}
          isConfigViewActive={isConfigViewActive}
          onViewMemory={handleViewMemory}
          isLoadingMemory={isLoadingMemory}
          currentVisitNumber={currentVisitNumber}
        />

        {/* Sticky helper note — pinned above the scrolling messages. Constrained to the
            same chat-content width (max-w-4xl) so it lines up with the message bubbles
            instead of spanning the whole screen. Nudges students to ask the coach
            whenever a word or term isn't clear. */}
        <div className="sticky top-0 z-30 flex justify-center px-3 pt-3 sm:px-6">
          <div className="mx-auto flex w-full max-w-4xl items-center justify-center gap-2 rounded-xl border border-violet-200 bg-violet-50/90 px-4 py-2 text-center text-sm font-bold text-violet-800 shadow-sm backdrop-blur-sm sm:text-base">
            <LightbulbRounded fontSize="small" className="shrink-0 text-violet-600" />
            <span>
              {language === 'hi'
                ? 'कोई शब्द या बात समझ में न आए? बेझिझक कोच से पूछें — वह कुछ भी समझाने में हमेशा खुश रहता है!'
                : "Don't understand a word or term? Feel free to ask the coach — it's happy to explain anything!"}
            </span>
          </div>
        </div>

        <main className="flex-1 p-2 sm:p-4 flex justify-center overflow-hidden relative">
          <div className="w-full max-w-4xl relative flex flex-col h-full">
            {/* Messages */}
            <MessageList
              messages={messages}
              isLoadingMessages={isLoadingMessages}
              isConfigViewActive={isConfigViewActive}
              chatError={chatError}
              isBrainProcessing={isBrainProcessing}
              isLoadingSteps={isLoadingSteps}
              showPipelineModal={showPipelineModal}
              onViewPipelineSteps={handleViewPipelineSteps}
              mode={mode}
              preparationStatus={preparationStatus}
              isPreparingConversation={isPreparingConversation}
              isDebugMode={isDebugMode}
              onTopicSelect={handleAutoSend}
            />
          </div>
        </main>

        {/* Message Input */}
        <MessageInput
          newMessage={newMessage}
          setNewMessage={setNewMessage}
          onSubmit={handleFormSubmit}
          onAutoSend={handleAutoSend}
          attachedPreview={attachedPreview}
          onPickImage={handlePickImage}
          onClearImage={clearAttachedImage}
          imageMode={imageMode}
          onChangeImageMode={setImageMode}
          isSendingMessage={isSendingMessage}
          isBrainProcessing={isBrainProcessing}
          isConfigViewActive={isConfigViewActive}
          isLoadingMessages={isLoadingMessages}
          isDisabled={isPreparingConversation || (currentConversationId !== null && messages.length === 0 && isLoadingMessages)}
          shouldShowSidebar={shouldShowSidebar}
          quizOpen={showQuizPanel}
          drawOpen={showDraw}
        />

        {/* Floating button — tap to draw directly on the chat (toggles draw mode). */}
        {!isConfigViewActive && !showDraw && (
          <button
            type="button"
            onClick={() => setShowDraw(true)}
            aria-label="Draw on the chat"
            title="Tap to draw on the chat"
            className="fixed bottom-28 right-6 z-[70] flex h-12 w-12 items-center justify-center rounded-full bg-indigo-600 text-white shadow-lg transition hover:bg-indigo-700"
          >
            <DrawRounded />
          </button>
        )}

        {/* Floating button — reopen the module-pages panel after it was closed. */}
        {!isConfigViewActive && !showDraw && stemPanelHidden && stemImages.length > 0 && (
          <button
            type="button"
            onClick={() => setStemPanelHidden(false)}
            aria-label="Show module pages"
            title="Show the module pages"
            className="fixed bottom-44 right-6 z-[70] flex h-12 w-12 items-center justify-center rounded-full bg-violet-600 text-white shadow-lg transition hover:bg-violet-700"
          >
            <CollectionsRounded />
          </button>
        )}
        </div>

        {/* Whiteboard — docks on the RIGHT of the chat when draw mode is on */}
        {showDraw && (
          <DrawCanvasOverlay
            onDone={(blob) => { handleNotepadSave(blob); setShowDraw(false); }}
            onCancel={() => setShowDraw(false)}
          />
        )}

        {/* STEM module pages — dock on the RIGHT when this chat is on a module
            that has rendered pages (and the whiteboard isn't open). */}
        {!showDraw && !stemPanelHidden && stemImages.length > 0 && (
          <StemImagePanel
            title={stemTitle}
            images={stemImages}
            onClose={() => setStemPanelHidden(true)}
          />
        )}
      </div>

      {/* Modals */}
      <ChatModals
        showPipelineModal={showPipelineModal}
        onClosePipelineModal={() => {
          setShowPipelineModal(false);
          setPipelineError(null);
        }}
        isLoadingSteps={isLoadingSteps}
        pipelineError={pipelineError}
        pipelineSteps={pipelineSteps}
        isDebugMode={isDebugMode}
        totalProcessingTime={totalProcessingTime}
        showMemoryModal={showMemoryModal}
        onCloseMemoryModal={() => {
          setShowMemoryModal(false);
          setMemoryError(null);
        }}
        isLoadingMemory={isLoadingMemory}
        memoryError={memoryError}
        memoryData={memoryData}
      />
      <FeedbackModal open={showFeedbackModal} onClose={() => setShowFeedbackModal(false)} />

      {currentConversationId && (
        <QuizPanel
          isOpen={showQuizPanel}
          onClose={() => setShowQuizPanel(false)}
          conversationId={currentConversationId}
        />
      )}
      
      {/* Debug Info - only shown when ?debug=true */}
      {isDebugMode && (
        <DebugInfo 
          visitNumber={currentVisitNumber} 
          promptVersionId={currentPromptVersionId}
          curiosityScore={curiosityScore}
        />
      )}

      {!isDebugMode && messages.length > 0 && (
        <CuriosityScoreIndicator
          score={curiosityScore}
          tip={curiosityTip}
          showQuiz={showQuizButton}
          quizLabel={quizButtonLabel}
          quizOpen={showQuizPanel}
          onQuizClick={
            currentConversationId
              ? () => setShowQuizPanel(true)
              : undefined
          }
        />
      )}

      {/* Exploration Panel - only shown when ?debug=true */}
      <ExplorationPanel
        isOpen={showExplorationPanel}
        onClose={() => setShowExplorationPanel(false)}
        directions={explorationDirections}
        prompt={explorationPrompt}
      />

          {/* Reopen button (only in debug mode when panel is collapsed) */}
      {isDebugMode && !showExplorationPanel && (
      <button
        onClick={() => setShowExplorationPanel(true)}
        className="fixed left-2 bottom-4 z-30 bg-white border border-gray-300 shadow-md text-xs sm:text-sm px-3 py-2 rounded hover:bg-gray-50"
        title="Show exploration directions"
      >
        Show exploration directions
      </button>
    )}
    {isDebugMode && !showExplorationPanel && (
  <button
    onClick={() => setShowExplorationPanel(true)}
    className="
      fixed left-0 top-4 z-30
      bg-white border border-l-0 border-gray-300 shadow-md
      text-[11px] sm:text-xs px-2 py-2
      rounded-r hover:bg-gray-50
    "
    aria-label="Show exploration directions"
    title="Show exploration directions"
  >
    Exploration Directions
  </button>
)}
    </div>
  );
};

export default ChatInterface;
